/**
 * The screen-design spec, v1 — the dashboard's copy of the contract
 * (DESIGN §4, `docs/screen-design/spec-v1.md`).
 *
 * The server is the source of truth; this mirrors its rules so a customer
 * finds out in the editor rather than on save, and both sides are held to the
 * same golden fixtures (`spec.test.ts`). `server/app/design/spec.py` and
 * `validate.py` are the files to change in step with this one.
 *
 * **v1.1** adds module contributions (MODULES §7): a button's action may be
 * `{"type": "module", "module", "action", "params"}`, a status row may name
 * `<module>.<value>`, and a block may be `{"id", "type": "module", "module",
 * "block", "props"}`. All three are checked against a `ContributionIndex` —
 * the catalogue the current release carries, merged with what the tenant
 * enabled. The document itself is unchanged (`"v"` stays 1), because an agent
 * that predates modules renders such an action as a disabled button through
 * its existing `Unknown` path and skips a block type it does not know.
 */

export const SPEC_VERSION = 1;

export const LIMITS = {
  blocks: { min: 1, max: 24 },
  flexSpacers: 3,
  assets: 6,
  assetBytes: 4 * 1024 * 1024,
  /** What one upload may weigh before the server re-encodes it — the same
   *  `MAX_UPLOAD_BYTES` the endpoint answers 413 on. Checked here so a dropped
   *  photo is refused in the editor rather than after a slow round trip. */
  uploadBytes: 12 * 1024 * 1024,
  url: 512,
  /** What a `string` param may hold, `min`/`max` or not (MODULES §2.8). */
  paramText: 512,
} as const;

/** By content on the server (§8); by the browser's own sniffing on a drop. */
export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

export const COLOR_RE = /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/;
export const ID_RE = /^[a-z0-9_-]{1,32}$/;
/** What a link's host may be made of, character for character the server's
 *  `HOST_RE` (`app/modules/props.py`): letters, digits, `.` and `-`, plus
 *  `[`/`]` for an IPv6 literal, `:` for a port and `_`, which intranet names
 *  do carry. Everything else is refused — `@`, so no userinfo, whitespace, and
 *  a non-ASCII (IDN) host, which the author writes in punycode. Not because
 *  the phone objects — it opens `https://пример.рф/` without complaint — but
 *  because `new URL` punycodes such a host and the server's `urlsplit` does
 *  not, so accepting it here would store one link and send another. */
export const HOST_RE = /^[A-Za-z0-9.\-[\]:_]+$/;

export const ALIGNS = ["left", "center", "right"] as const;
export const WEIGHTS = ["regular", "medium", "bold"] as const;
export const FONTS = ["sans", "serif", "mono"] as const;
export const VARIANTS = ["filled", "outlined", "text"] as const;
export const BUTTON_SIZES = ["sm", "md", "lg"] as const;
export const STATUS_ROWS = [
  "enrollment",
  "accessibility",
  "capture",
  "version",
  "tenant",
] as const;
export const VERTICAL_ALIGNS = ["top", "center", "bottom"] as const;
export const GRADIENT_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315] as const;
export const BLOCK_TYPES = [
  "text",
  "button",
  "image",
  "spacer",
  "divider",
  "status",
] as const;

/** The whole action catalogue (§4.4). Nothing else is accepted. */
export const ACTIONS = [
  "enable_accessibility",
  "ignore_battery_optimization",
  "allow_screen_capture",
  "open_url",
  "open_app_settings",
  "copy_device_id",
  "forget_enrollment",
] as const;

export const REQUIRED_ACTION = "enable_accessibility";

/** The action a module contributes, and its four keys (MODULES §7.1). */
export const MODULE_ACTION = "module";
export const MODULE_ACTION_KEYS = ["type", "module", "action", "params"];
/**
 * The block a module contributes (MODULES §7.4). Deliberately *not* in
 * `BLOCK_TYPES`: that list is the closed set of core types the spec writes
 * down, and this one names a view whose properties only a manifest can
 * describe. Five keys, `id` and `type` included — a block *is* the object, so
 * unlike an action it carries its own id.
 */
export const MODULE_BLOCK = "module";
export const MODULE_BLOCK_KEYS = ["id", "type", "module", "block", "props"];
/** The manifest's own id shape, checked here so the validator stands alone. */
export const MODULE_ID_RE = /^[a-z][a-z0-9_]{1,23}$/;
export const MODULE_ROW_RE = /^[a-z][a-z0-9_]{1,23}\.[a-z][a-z0-9_]{1,23}$/;

export type Align = (typeof ALIGNS)[number];
export type Weight = (typeof WEIGHTS)[number];
export type Font = (typeof FONTS)[number];
export type Variant = (typeof VARIANTS)[number];
export type ButtonSize = (typeof BUTTON_SIZES)[number];
export type StatusRow = (typeof STATUS_ROWS)[number];
export type VerticalAlign = (typeof VERTICAL_ALIGNS)[number];
export type BlockType = (typeof BLOCK_TYPES)[number];
export type ActionType = (typeof ACTIONS)[number];

/** `{"type": "module", "module": "location", "action": "get_fix"}` — at most
 *  four keys, both ids matching `MODULE_ID_RE` (MODULES §7.1). */
export interface ModuleActionRef {
  type: typeof MODULE_ACTION;
  module: string;
  action: string;
  /** This button's own values for the action's `params` (MODULES §2.6);
   *  absent is `{}`. Stored as the author wrote it — the phone fills the
   *  manifest's defaults, so a design never freezes today's default. */
  params?: Record<string, unknown>;
}

export type Action =
  | { type: Exclude<ActionType, "open_url"> }
  | { type: "open_url"; url: string }
  | ModuleActionRef;

/**
 * A status row: one of the core five, or `<module>.<value>` naming a value a
 * module declares with `status_row: true` (MODULES §7.2).
 */
export type RowRef = StatusRow | `${string}.${string}`;

/** Is this row a module's rather than one of the core five? */
export function isModuleRow(row: string): boolean {
  return !STATUS_ROWS.includes(row as StatusRow) && MODULE_ROW_RE.test(row);
}

/**
 * What a release offers and what this customer switched on, as the validator
 * asks it (MODULES §7.3). Built from `GET /modules` by `design/modules.ts`;
 * plain data rather than a class, so a test can hand-write one.
 */
export interface ContributionIndex {
  /** Every module the release carries — enabled or not. */
  known: ReadonlySet<string>;
  enabled: ReadonlySet<string>;
  /** Module id -> its actions, each with the `params` it declares: a button's
   *  own values are checked against them (MODULES §7.3). */
  actions: ReadonlyMap<string, ReadonlyMap<string, ActionSpec>>;
  /** Module id -> its blocks, each with the `props` it declares — the same
   *  arg specs an action's `params` are made of (MODULES §7.4). */
  blocks: ReadonlyMap<string, ReadonlyMap<string, BlockSpec>>;
  /** Module id -> the value ids that may be a status row. */
  rows: ReadonlyMap<string, ReadonlySet<string>>;
}

/**
 * One arg spec of the manifest's closed `kind` schema (MODULES §2.8) — an
 * action's `params` and a command's `args` are made of the same thing.
 *
 * Written here rather than imported from `api/types` so the validator stands
 * alone with the rest of the contract; `ModuleArg` is assignable to it.
 */
export interface ParamSpec {
  kind: string;
  required?: boolean;
  default?: unknown;
  /** Bounds on `int`/`float`, length bounds on `string`. */
  min?: number;
  max?: number;
  /** `enum` only. */
  choices?: string[];
}

/** As much of a manifest action as the validator and the editor's form read. */
export interface ActionSpec {
  params?: Record<string, ParamSpec>;
}

/** As much of a manifest block as the validator and the preview read (§2.11). */
export interface BlockSpec {
  /** `flex` takes the room the rest of the stack leaves; `content` sits in it. */
  height?: "content" | "flex";
  props?: Record<string, ParamSpec>;
}

export interface ImageBlock {
  id: string;
  type: "image";
  asset: string;
  width?: number;
  fullWidth?: boolean;
  align?: Align;
  radius?: number;
  alt?: string;
  /**
   * **Not part of the spec** — an object URL for a file the editor is still
   * uploading, so a dropped picture is on the phone before the round trip
   * finishes. `settled()` strips it, and every path that validates, saves or
   * drafts the document goes through that: the server never sees this key.
   */
  pendingSrc?: string;
}

export interface TextBlock {
  id: string;
  type: "text";
  text: string;
  size?: number;
  weight?: Weight;
  color?: string;
  align?: Align;
  font?: Font;
}

export interface ButtonBlock {
  id: string;
  type: "button";
  label: string;
  action: Action;
  variant?: Variant;
  color?: string;
  textColor?: string;
  radius?: number;
  fullWidth?: boolean;
  size?: ButtonSize;
  /** Meaningful only when `fullWidth` is false — a button that fills the row
   *  has nowhere to sit (§2 button). */
  align?: Align;
}

export interface SpacerBlock {
  id: string;
  type: "spacer";
  height?: number;
  flex?: boolean;
}

export interface DividerBlock {
  id: string;
  type: "divider";
  color?: string;
  thickness?: number;
  inset?: number;
}

export interface StatusBlock {
  id: string;
  type: "status";
  rows?: RowRef[];
  color?: string;
  mutedColor?: string;
  size?: number;
}

/**
 * `{"id": "site", "type": "module", "module": "webwrapper", "block": "page",
 *   "props": {"url": "https://example.com/"}}` — a view a module draws inside
 * the customer's screen (MODULES §7.4).
 *
 * The core types describe themselves; this one is a *reference*, and what may
 * be inside `props` comes from the module's manifest rather than from here.
 */
export interface ModuleBlockRef {
  id: string;
  type: typeof MODULE_BLOCK;
  module: string;
  block: string;
  /** This instance's own values for the block's `props`; absent is `{}`.
   *  Stored as the author wrote it — the phone fills the manifest's defaults. */
  props?: Record<string, unknown>;
}

export type Block =
  | ImageBlock
  | TextBlock
  | ButtonBlock
  | SpacerBlock
  | DividerBlock
  | StatusBlock
  | ModuleBlockRef;

export type Background =
  | { type: "solid"; color: string }
  | { type: "gradient"; colors: string[]; angle?: number }
  | { type: "image"; asset: string; scale?: "cover" | "contain"; overlay?: string };

export interface Theme {
  background?: Background;
  statusBarIcons?: "light" | "dark";
  padding?: number;
  gap?: number;
  verticalAlign?: VerticalAlign;
}

export interface Spec {
  v: number;
  theme?: Theme;
  blocks: Block[];
}

/** Every property's range and default, in one table — §4.2 and §4.3. */
export const RANGES = {
  image: { width: [24, 360], radius: [0, 48], alt: [0, 80] },
  text: { size: [10, 48], text: [1, 300] },
  button: { radius: [0, 32], label: [1, 40] },
  spacer: { height: [4, 200] },
  divider: { thickness: [1, 4], inset: [0, 48] },
  status: { size: [12, 16] },
  theme: { padding: [0, 48], gap: [0, 48] },
} as const;

export const DEFAULTS = {
  theme: {
    background: { type: "solid", color: "#000000" } as Background,
    statusBarIcons: "light" as const,
    padding: 24,
    gap: 16,
    verticalAlign: "center" as VerticalAlign,
  },
  image: { width: 120, fullWidth: false, align: "center" as Align, radius: 0, alt: "" },
  text: {
    size: 16,
    weight: "regular" as Weight,
    color: "#FFFFFF",
    align: "center" as Align,
    font: "sans" as Font,
  },
  button: {
    variant: "filled" as Variant,
    color: "#5B8DEE",
    textColor: "#FFFFFF",
    radius: 12,
    fullWidth: true,
    size: "md" as ButtonSize,
    align: "center" as Align,
  },
  spacer: { height: 16, flex: false },
  divider: { color: "#FFFFFF33", thickness: 1, inset: 0 },
  status: {
    rows: ["enrollment", "accessibility"] as RowRef[],
    color: "#FFFFFF",
    mutedColor: "#8B93A7",
    size: 13,
  },
} as const;

/** Button heights in dp, by size (§4.2). */
export const BUTTON_HEIGHTS: Record<ButtonSize, number> = { sm: 40, md: 48, lg: 56 };

export interface Issue {
  level: string;
  code: string;
  message: string;
  path: string;
}

let counter = 0;

/** A fresh id: short, stable, and inside `ID_RE`. */
export function newId(type: string): string {
  counter += 1;
  return `${type}_${Date.now().toString(36).slice(-4)}${counter.toString(36)}`;
}

/** A block of `type` with every default filled in, ready to drop into a design. */
export function newBlock(type: BlockType): Block {
  const id = newId(type);
  switch (type) {
    case "text":
      return { id, type, text: "New text", ...DEFAULTS.text };
    case "button":
      return {
        id,
        type,
        label: "New button",
        action: { type: "open_app_settings" },
        ...DEFAULTS.button,
      };
    case "image":
      return { id, type, asset: "", ...DEFAULTS.image };
    case "spacer":
      return { id, type, ...DEFAULTS.spacer };
    case "divider":
      return { id, type, ...DEFAULTS.divider };
    case "status":
      return { id, type, ...DEFAULTS.status, rows: [...DEFAULTS.status.rows] };
  }
}

/**
 * The block a module contributes, ready to drop into a design (MODULES §7.4).
 *
 * `props` is seeded by the caller from the manifest, exactly as a picked
 * action's `params` are — and left out entirely when there is nothing to seed,
 * so the document says only what its author wrote.
 */
export function newModuleBlock(
  module: string,
  block: string,
  props?: Record<string, unknown>,
): ModuleBlockRef {
  const next: ModuleBlockRef = { id: newId(block), type: MODULE_BLOCK, module, block };
  if (props && Object.keys(props).length > 0) next.props = props;
  return next;
}

/**
 * An image block for a file that is still uploading (§9.3).
 *
 * It carries the object URL rather than an asset id, so the picture is on the
 * phone the moment it is dropped; `settleImage` swaps in the asset when the
 * upload lands, and `cancelImport` takes the block away again if it does not.
 */
export function pendingImage(src: string): ImageBlock {
  return { id: newId("image"), type: "image", asset: "", ...DEFAULTS.image, pendingSrc: src };
}

/** A block whose picture exists only in this browser, so far. */
export function isPendingImage(block: Block): block is ImageBlock {
  return block.type === "image" && typeof block.pendingSrc === "string";
}

/**
 * The document as the server must see it: no `pendingSrc` anywhere, and no
 * block that has nothing but one.
 *
 * Everything that validates, saves or drafts a design goes through this, so an
 * upload in flight can never reach the API, be counted as a dangling asset, or
 * be restored from a draft as a blob URL that died with the tab.
 */
export function settled(spec: Spec): Spec {
  const blocks: Block[] = [];
  for (const block of spec.blocks ?? []) {
    if (!isPendingImage(block)) {
      blocks.push(block);
      continue;
    }
    // A replacement keeps the picture it is replacing until the new one lands.
    if (!block.asset) continue;
    const rest = { ...block };
    delete rest.pendingSrc;
    blocks.push(rest);
  }
  return { ...spec, blocks };
}

function issue(code: string, message: string, path: string): Issue {
  return { level: "error", code, message, path };
}

/** A `design_module_disabled` — saved happily, refused at build (MODULES §7.3). */
function warning(code: string, message: string, path: string): Issue {
  return { level: "warning", code, message, path };
}

/** What stops a document being saved. */
export function errorsOf(issues: Issue[]): Issue[] {
  return issues.filter((entry) => entry.level !== "warning");
}

/** What the editor shows under the errors and the *build* refuses on. */
export function warningsOf(issues: Issue[]): Issue[] {
  return issues.filter((entry) => entry.level === "warning");
}

/**
 * `blocks[logo]` when the block can be addressed by id, else `blocks[3]` (§7).
 *
 * A `module` block earns the id path too: its type is known — it is the one
 * type whose *contents* need a catalogue, and the editor selects it by id
 * exactly as it selects a text block.
 */
function blockPath(index: number, block: unknown): string {
  const candidate = block as { id?: unknown; type?: unknown };
  const known =
    BLOCK_TYPES.includes(candidate?.type as BlockType) || candidate?.type === MODULE_BLOCK;
  if (typeof candidate?.id === "string" && ID_RE.test(candidate.id) && known) {
    return `blocks[${candidate.id}]`;
  }
  return `blocks[${index}]`;
}

/**
 * Every reason this document cannot be saved, in reading order — the same
 * codes and paths the server produces, so the editor's list and a server 422
 * say the same thing. Warnings ride in the same list and carry
 * `level: "warning"`; split them with `errorsOf()` and `warningsOf()`.
 *
 * `index` is the module catalogue (MODULES §7.3). **Without one a well-formed
 * module action or row is accepted unchecked** — that is for a caller with no
 * catalogue to check against, such as the fixture parity tests, or the editor
 * while `GET /modules` is still in flight; nothing is worse than telling a
 * customer their design is broken because a list had not arrived.
 */
export function validateSpec(
  spec: unknown,
  assetIds: Set<string> = new Set(),
  index?: ContributionIndex,
): Issue[] {
  const issues: Issue[] = [];
  if (typeof spec !== "object" || spec === null || Array.isArray(spec)) {
    return [issue("design_bad_version", "A design must be an object", "")];
  }
  const doc = spec as Record<string, unknown>;

  if (doc.v !== SPEC_VERSION) {
    issues.push(issue("design_bad_version", `Only spec version ${SPEC_VERSION} is supported`, "v"));
  }
  for (const key of Object.keys(doc).sort()) {
    if (!["v", "theme", "blocks"].includes(key)) {
      issues.push(issue("design_bad_value", `Unknown property '${key}'`, key));
    }
  }
  issues.push(...validateTheme(doc.theme, assetIds));
  issues.push(...validateBlocks(doc.blocks, assetIds, index));
  return issues;
}

function color(value: unknown, path: string): Issue[] {
  return typeof value === "string" && COLOR_RE.test(value)
    ? []
    : [issue("design_bad_value", "Colours look like #RRGGBB or #RRGGBBAA", path)];
}

function num(value: unknown, [min, max]: readonly [number, number], path: string): Issue[] {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    return [issue("design_bad_value", "Expected a whole number", path)];
  }
  if (value < min) return [issue("design_bad_value", `Must be at least ${min}`, path)];
  if (value > max) return [issue("design_bad_value", `Must be at most ${max}`, path)];
  return [];
}

function oneOf(value: unknown, allowed: readonly string[], path: string): Issue[] {
  return allowed.includes(value as string)
    ? []
    : [issue("design_bad_value", `Must be one of ${allowed.join(", ")}`, path)];
}

function str(value: unknown, [min, max]: readonly [number, number], path: string): Issue[] {
  if (typeof value !== "string") return [issue("design_bad_value", "Expected text", path)];
  if (value.length < min) return [issue("design_bad_value", "This cannot be empty", path)];
  if (value.length > max) {
    return [issue("design_bad_value", `At most ${max} characters`, path)];
  }
  return [];
}

function bool(value: unknown, path: string): Issue[] {
  return typeof value === "boolean"
    ? []
    : [issue("design_bad_value", "Expected true or false", path)];
}

const THEME_KEYS = ["background", "statusBarIcons", "padding", "gap", "verticalAlign"];

function validateTheme(theme: unknown, assets: Set<string>): Issue[] {
  if (theme === undefined || theme === null) return [];
  if (typeof theme !== "object" || Array.isArray(theme)) {
    return [issue("design_bad_value", "theme must be an object", "theme")];
  }
  const node = theme as Record<string, unknown>;
  const issues: Issue[] = [];
  for (const key of Object.keys(node).sort()) {
    if (!THEME_KEYS.includes(key)) {
      issues.push(issue("design_bad_value", `Unknown theme property '${key}'`, `theme.${key}`));
    }
  }
  if (node.background !== undefined && node.background !== null) {
    issues.push(...validateBackground(node.background, assets));
  }
  if (node.statusBarIcons !== undefined && node.statusBarIcons !== null) {
    issues.push(...oneOf(node.statusBarIcons, ["light", "dark"], "theme.statusBarIcons"));
  }
  if (node.padding !== undefined && node.padding !== null) {
    issues.push(...num(node.padding, RANGES.theme.padding, "theme.padding"));
  }
  if (node.gap !== undefined && node.gap !== null) {
    issues.push(...num(node.gap, RANGES.theme.gap, "theme.gap"));
  }
  if (node.verticalAlign !== undefined && node.verticalAlign !== null) {
    issues.push(...oneOf(node.verticalAlign, VERTICAL_ALIGNS, "theme.verticalAlign"));
  }
  return issues;
}

const BACKGROUND_KEYS: Record<string, string[]> = {
  solid: ["type", "color"],
  gradient: ["type", "colors", "angle"],
  image: ["type", "asset", "scale", "overlay"],
};

function validateBackground(background: unknown, assets: Set<string>): Issue[] {
  const path = "theme.background";
  if (typeof background !== "object" || background === null || Array.isArray(background)) {
    return [issue("design_bad_value", "background must be an object", path)];
  }
  const node = background as Record<string, unknown>;
  const kind = node.type as string;
  if (!Object.keys(BACKGROUND_KEYS).includes(kind)) {
    return [
      issue(
        "design_bad_value",
        `background type must be one of ${Object.keys(BACKGROUND_KEYS).join(", ")}`,
        `${path}.type`,
      ),
    ];
  }
  const issues: Issue[] = [];
  for (const key of Object.keys(node).sort()) {
    if (!BACKGROUND_KEYS[kind].includes(key)) {
      issues.push(
        issue("design_bad_value", `Unknown background property '${key}'`, `${path}.${key}`),
      );
    }
  }
  if (kind === "solid") {
    issues.push(...color(node.color, `${path}.color`));
  } else if (kind === "gradient") {
    const colors = node.colors;
    if (!Array.isArray(colors) || colors.length < 2 || colors.length > 3) {
      issues.push(issue("design_bad_value", "A gradient needs 2 or 3 colours", `${path}.colors`));
    } else {
      colors.forEach((value, index) => issues.push(...color(value, `${path}.colors[${index}]`)));
    }
    const angle = node.angle === undefined ? 180 : node.angle;
    if (!GRADIENT_ANGLES.includes(angle as never)) {
      issues.push(
        issue("design_bad_value", `angle must be one of ${GRADIENT_ANGLES.join(", ")}`, `${path}.angle`),
      );
    }
  } else {
    if (typeof node.asset !== "string" || !node.asset) {
      issues.push(issue("design_bad_value", "An image background needs an asset", `${path}.asset`));
    } else if (!assets.has(node.asset)) {
      issues.push(
        issue(
          "design_dangling_asset",
          "That image is not part of this design any more",
          `${path}.asset`,
        ),
      );
    }
    const scale = node.scale === undefined ? "cover" : node.scale;
    if (!["cover", "contain"].includes(scale as string)) {
      issues.push(issue("design_bad_value", "scale must be cover or contain", `${path}.scale`));
    }
    if (node.overlay !== undefined && node.overlay !== null) {
      issues.push(...color(node.overlay, `${path}.overlay`));
    }
  }
  return issues;
}

const BLOCK_KEYS: Record<BlockType, string[]> = {
  image: ["id", "type", "asset", "width", "fullWidth", "align", "radius", "alt"],
  text: ["id", "type", "text", "size", "weight", "color", "align", "font"],
  button: [
    "id",
    "type",
    "label",
    "action",
    "variant",
    "color",
    "textColor",
    "radius",
    "fullWidth",
    "size",
    "align",
  ],
  spacer: ["id", "type", "height", "flex"],
  divider: ["id", "type", "color", "thickness", "inset"],
  status: ["id", "type", "rows", "color", "mutedColor", "size"],
};

// `catalog` rather than `index` here: the loop below already owns `index`,
// and a block's position and the module catalogue are not the same thing.
function validateBlocks(
  blocks: unknown,
  assets: Set<string>,
  catalog?: ContributionIndex,
): Issue[] {
  if (!Array.isArray(blocks)) {
    return [issue("design_bad_block", "blocks must be a list", "blocks")];
  }
  if (blocks.length < LIMITS.blocks.min) {
    return [issue("design_too_many_blocks", "A design needs at least one block", "blocks")];
  }

  const issues: Issue[] = [];
  if (blocks.length > LIMITS.blocks.max) {
    issues.push(
      issue(
        "design_too_many_blocks",
        `A design may hold at most ${LIMITS.blocks.max} blocks`,
        "blocks",
      ),
    );
  }

  const seen = new Set<string>();
  let flexSpacers = 0;
  let hasAccessibility = false;
  let hasModuleBlock = false;

  blocks.forEach((raw, index) => {
    const path = blockPath(index, raw);
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
      issues.push(issue("design_bad_block", "A block must be an object", path));
      return;
    }
    const block = raw as Record<string, unknown>;

    const id = block.id;
    if (typeof id !== "string" || !ID_RE.test(id)) {
      issues.push(
        issue(
          "design_bad_block",
          "A block id must be 1-32 characters of a-z, 0-9, '-' or '_'",
          `${path}.id`,
        ),
      );
    } else if (seen.has(id)) {
      // By index: the id names two blocks, so it cannot say which one to select.
      issues.push(
        issue("design_duplicate_id", `Two blocks share the id '${id}'`, `blocks[${index}].id`),
      );
    } else {
      seen.add(id);
    }

    const type = block.type as BlockType;
    if (block.type === MODULE_BLOCK) {
      // Not a core type, so nothing in `BLOCK_KEYS` describes it: what its
      // properties are is the module's manifest to say (MODULES §7.4). A flex
      // one is not a spacer either, so it never counts toward that limit.
      hasModuleBlock = true;
      issues.push(...validateModuleBlock(block, path, catalog));
      return;
    }
    if (!BLOCK_TYPES.includes(type)) {
      issues.push(
        issue("design_bad_block", `Unknown block type '${block.type}'`, `blocks[${index}].type`),
      );
      return;
    }
    for (const key of Object.keys(block).sort()) {
      if (!BLOCK_KEYS[type].includes(key)) {
        issues.push(issue("design_bad_block", `Unknown property '${key}'`, `${path}.${key}`));
      }
    }
    issues.push(...validateBlockProps(type, block, path, assets, catalog));

    if (type === "spacer" && block.flex === true) flexSpacers += 1;
    if (type === "button") {
      const action = block.action as { type?: string } | undefined;
      if (action?.type === REQUIRED_ACTION) hasAccessibility = true;
    }
  });

  if (flexSpacers > LIMITS.flexSpacers) {
    issues.push(
      issue(
        "design_too_many_blocks",
        `At most ${LIMITS.flexSpacers} spacers may be flexible`,
        "blocks",
      ),
    );
  }
  if (!hasAccessibility) {
    // An error for a screen the customer composed out of core blocks — there
    // is always room for one more button, and without it the agent can never
    // be switched on. A **warning** once a module block is on the screen
    // (MODULES §7.4): a full-screen page is the whole screen by design, and
    // refusing it would mean the constructor cannot express the thing modules
    // were lifted for. The customer is still told; the build is not blocked,
    // because accessibility can be granted from Android's own settings.
    const message =
      "Add a button whose action is 'Enable accessibility' — without it the agent " +
      "can never be switched on";
    issues.push(
      hasModuleBlock
        ? warning("design_no_accessibility_button", message, "blocks")
        : issue("design_no_accessibility_button", message, "blocks"),
    );
  }
  return issues;
}

function validateBlockProps(
  type: BlockType,
  block: Record<string, unknown>,
  path: string,
  assets: Set<string>,
  index?: ContributionIndex,
): Issue[] {
  const issues: Issue[] = [];
  const has = (key: string) => block[key] !== undefined && block[key] !== null;

  if (type === "image") {
    if (typeof block.asset !== "string" || !block.asset) {
      issues.push(issue("design_bad_value", "An image block needs an asset", `${path}.asset`));
    } else if (!assets.has(block.asset)) {
      issues.push(
        issue(
          "design_dangling_asset",
          "That image is not part of this design any more",
          `${path}.asset`,
        ),
      );
    }
    if (has("width")) issues.push(...num(block.width, RANGES.image.width, `${path}.width`));
    if (has("fullWidth")) issues.push(...bool(block.fullWidth, `${path}.fullWidth`));
    if (has("align")) issues.push(...oneOf(block.align, ALIGNS, `${path}.align`));
    if (has("radius")) issues.push(...num(block.radius, RANGES.image.radius, `${path}.radius`));
    if (has("alt")) issues.push(...str(block.alt, [0, 80], `${path}.alt`));
  } else if (type === "text") {
    if (!has("text")) {
      issues.push(issue("design_bad_value", "'text' is required", `${path}.text`));
    } else {
      issues.push(...str(block.text, RANGES.text.text, `${path}.text`));
    }
    if (has("size")) issues.push(...num(block.size, RANGES.text.size, `${path}.size`));
    if (has("weight")) issues.push(...oneOf(block.weight, WEIGHTS, `${path}.weight`));
    if (has("color")) issues.push(...color(block.color, `${path}.color`));
    if (has("align")) issues.push(...oneOf(block.align, ALIGNS, `${path}.align`));
    if (has("font")) issues.push(...oneOf(block.font, FONTS, `${path}.font`));
  } else if (type === "button") {
    if (!has("label")) {
      issues.push(issue("design_bad_value", "'label' is required", `${path}.label`));
    } else {
      issues.push(...str(block.label, RANGES.button.label, `${path}.label`));
    }
    if (!has("action")) {
      issues.push(issue("design_bad_value", "'action' is required", `${path}.action`));
    } else {
      issues.push(...validateAction(block.action, `${path}.action`, index));
    }
    if (has("variant")) issues.push(...oneOf(block.variant, VARIANTS, `${path}.variant`));
    if (has("color")) issues.push(...color(block.color, `${path}.color`));
    if (has("textColor")) issues.push(...color(block.textColor, `${path}.textColor`));
    if (has("radius")) issues.push(...num(block.radius, RANGES.button.radius, `${path}.radius`));
    if (has("fullWidth")) issues.push(...bool(block.fullWidth, `${path}.fullWidth`));
    if (has("size")) issues.push(...oneOf(block.size, BUTTON_SIZES, `${path}.size`));
    if (has("align")) issues.push(...oneOf(block.align, ALIGNS, `${path}.align`));
  } else if (type === "spacer") {
    if (has("height")) issues.push(...num(block.height, RANGES.spacer.height, `${path}.height`));
    if (has("flex")) issues.push(...bool(block.flex, `${path}.flex`));
  } else if (type === "divider") {
    if (has("color")) issues.push(...color(block.color, `${path}.color`));
    if (has("thickness")) {
      issues.push(...num(block.thickness, RANGES.divider.thickness, `${path}.thickness`));
    }
    if (has("inset")) issues.push(...num(block.inset, RANGES.divider.inset, `${path}.inset`));
  } else if (type === "status") {
    if (has("rows")) issues.push(...validateRows(block.rows, `${path}.rows`, index));
    if (has("color")) issues.push(...color(block.color, `${path}.color`));
    if (has("mutedColor")) issues.push(...color(block.mutedColor, `${path}.mutedColor`));
    if (has("size")) issues.push(...num(block.size, RANGES.status.size, `${path}.size`));
  }
  return issues;
}

function validateAction(action: unknown, path: string, index?: ContributionIndex): Issue[] {
  if (typeof action !== "object" || action === null || Array.isArray(action)) {
    return [issue("design_bad_action", "An action must be an object", path)];
  }
  const node = action as Record<string, unknown>;
  const kind = node.type as ActionType;
  if (node.type === MODULE_ACTION) return validateModuleAction(node, path, index);
  if (!ACTIONS.includes(kind)) {
    return [issue("design_bad_action", `Unknown action '${node.type}'`, `${path}.type`)];
  }
  const allowed = kind === "open_url" ? ["type", "url"] : ["type"];
  const issues: Issue[] = [];
  for (const key of Object.keys(node).sort()) {
    if (!allowed.includes(key)) {
      issues.push(issue("design_bad_action", `Unknown action property '${key}'`, `${path}.${key}`));
    }
  }
  if (kind !== "open_url") return issues;
  return [
    ...issues,
    ...checkUrl(node.url, `${path}.url`, "design_bad_action", "This action needs a link"),
  ];
}

/**
 * The link rule, in one place (R20): a literal lowercase `https://`, at most
 * `LIMITS.url` characters, and a host inside `HOST_RE` — ASCII, so neither an
 * IDN nor a `user:pass@`. `open_url`'s own url and every `url`-kind param ask
 * the same question — the server shares one function for the two as well
 * (`app/modules/props.py`), which is what keeps the two sides saying the same
 * thing about the same link.
 *
 * `missing` is the wording for an empty one, because the two callers word that
 * differently: an action *needs* a link, a parameter *expects* one.
 */
export function checkUrl(
  value: unknown,
  path: string,
  code: string,
  missing: string,
): Issue[] {
  if (typeof value !== "string" || !value) return [issue(code, missing, path)];
  if (value.length > LIMITS.url) {
    return [issue(code, `A link may be ${LIMITS.url} characters`, path)];
  }
  // The prefix is read literally, off the raw string, because both `new URL`
  // and the server's `urlsplit` are kinder than the fleet: `new URL` repairs
  // `https:example.com` into `https://example.com/`, and both of them read
  // `HTTPS://example.com` as the https scheme. This link opens in a browser on
  // a phone somebody else administers, so it is written the one way or not at
  // all — and the server matches on this same literal.
  if (!value.startsWith("https://")) {
    return [issue(code, "Links must start with https://", path)];
  }
  // The authority as the author wrote it — everything up to the first `/`, `?`
  // or `#` — read before `new URL` normalises it. `new URL` punycodes an IDN
  // host, so `https://пример.рф` would pass here and be refused by the server,
  // whose `HOST_RE` is ASCII: the disagreement is between these two, not with
  // the phone, which opens an IDN host quite happily (`isOpenable` goes
  // through Android's `Uri`). The author writes the punycode form and the two
  // sides then store the same bytes. The space and the `user:pass@` fall out
  // of the same character class.
  const authority = value.slice("https://".length).split(/[/?#]/)[0];
  if (!HOST_RE.test(authority)) {
    return [issue(code, "That is not a usable link", path)];
  }
  try {
    // Still worth asking: a malformed IPv6 literal is inside `HOST_RE` and
    // nowhere near dialable, and `new URL` is what knows the difference.
    new URL(value);
  } catch {
    return [issue(code, "That is not a usable link", path)];
  }
  return [];
}

// --- module contributions (v1.1, MODULES §7) -------------------------------

/**
 * `{"type": "module", "module": "webwrapper", "action": "open",
 *   "params": {"url": "https://example.com"}}`.
 *
 * `design_bad_action` is the v1 code reused: an unknown `action.type` already
 * produces it, and a malformed module action is the same class of mistake.
 * What is *inside* `params` is `design_bad_param`, because that needs the
 * catalogue and the shape does not.
 */
function validateModuleAction(
  node: Record<string, unknown>,
  path: string,
  index?: ContributionIndex,
): Issue[] {
  const issues: Issue[] = [];
  for (const key of Object.keys(node).sort()) {
    if (!MODULE_ACTION_KEYS.includes(key)) {
      issues.push(issue("design_bad_action", `Unknown action property '${key}'`, `${path}.${key}`));
    }
  }
  const moduleId = node.module;
  const actionId = node.action;
  const params = node.params;
  if (typeof moduleId !== "string" || !MODULE_ID_RE.test(moduleId)) {
    issues.push(issue("design_bad_action", "This action needs a module", `${path}.module`));
  }
  if (typeof actionId !== "string" || !MODULE_ID_RE.test(actionId)) {
    issues.push(issue("design_bad_action", "This action needs an action", `${path}.action`));
  }
  if (params !== undefined && !isRecord(params)) {
    issues.push(issue("design_bad_action", "Parameters must be a JSON object", `${path}.params`));
  }
  // No catalogue to check against: a well-formed reference is accepted, and
  // the build — which always has one — is what refuses it.
  if (issues.length > 0 || !index) return issues;

  const module = moduleId as string;
  const action = actionId as string;
  if (!index.known.has(module)) {
    return [
      issue("design_unknown_module", `This release has no module '${module}'`, `${path}.module`),
    ];
  }
  const spec = index.actions.get(module)?.get(action);
  if (!spec) {
    return [
      issue(
        "design_unknown_contribution",
        `'${module}' has no action '${action}'`,
        `${path}.action`,
      ),
    ];
  }
  if (!index.enabled.has(module)) {
    // A warning, not an error: the design stays saved and the editor shows a
    // banner. The *build* is where this becomes a refusal (MODULES §7.3). Its
    // params are still checked — the customer switches the module back on and
    // the button has to work.
    issues.push(
      warning(
        "design_module_disabled",
        `'${module}' is switched off, so this button will do nothing`,
        `${path}.module`,
      ),
    );
  }
  return [
    ...issues,
    ...validateParams(spec.params ?? {}, isRecord(params) ? params : {}, `${path}.params`),
  ];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * `{"id": "site", "type": "module", "module": "webwrapper", "block": "page",
 *   "props": {"url": "https://example.com/"}}`.
 *
 * The same three steps a module action goes through (MODULES §7.1), one level
 * up: the *shape* is `design_bad_block` because it needs no catalogue, the
 * *reference* is `design_unknown_module` / `design_unknown_contribution`, and
 * what is inside `props` is `design_bad_param` against the schema the block
 * declares — the same arg specs an action's `params` are made of.
 */
function validateModuleBlock(
  block: Record<string, unknown>,
  path: string,
  index?: ContributionIndex,
): Issue[] {
  const issues: Issue[] = [];
  for (const key of Object.keys(block).sort()) {
    if (!MODULE_BLOCK_KEYS.includes(key)) {
      issues.push(issue("design_bad_block", `Unknown property '${key}'`, `${path}.${key}`));
    }
  }
  const moduleId = block.module;
  const blockId = block.block;
  const props = block.props;
  if (typeof moduleId !== "string" || !MODULE_ID_RE.test(moduleId)) {
    issues.push(issue("design_bad_block", "This block needs a module", `${path}.module`));
  }
  if (typeof blockId !== "string" || !MODULE_ID_RE.test(blockId)) {
    issues.push(issue("design_bad_block", "This block needs a block", `${path}.block`));
  }
  if (props !== undefined && !isRecord(props)) {
    // *Present* is what counts, exactly as it does for an action's `params`:
    // `"props": null` is a mistake somebody wrote down.
    issues.push(issue("design_bad_block", "Properties must be a JSON object", `${path}.props`));
  }
  if (issues.length > 0 || !index) return issues;

  const module = moduleId as string;
  const name = blockId as string;
  if (!index.known.has(module)) {
    return [
      issue("design_unknown_module", `This release has no module '${module}'`, `${path}.module`),
    ];
  }
  const spec = index.blocks.get(module)?.get(name);
  if (!spec) {
    return [
      issue(
        "design_unknown_contribution",
        `'${module}' has no block '${name}'`,
        `${path}.block`,
      ),
    ];
  }
  if (!index.enabled.has(module)) {
    // A warning, like a disabled module's button: the design stays saved and
    // the editor shows a banner. Its props are still checked — the customer
    // switches the module back on and the block has to work (MODULES §7.3).
    issues.push(
      warning(
        "design_module_disabled",
        `'${module}' is switched off, so this block will be missing`,
        `${path}.module`,
      ),
    );
  }
  return [
    ...issues,
    ...validateParams(spec.props ?? {}, isRecord(props) ? props : {}, `${path}.props`),
  ];
}

/**
 * A button's `params` against the schema the manifest gives that action
 * (MODULES §2.6). Every mistake, not just the first: the form the editor
 * generated from this same schema shows them all at once.
 *
 * What the phone will actually receive is deliberately *not* built here. A
 * design is stored as its author wrote it and the phone fills the defaults out
 * of the manifest it carries, so filling them in would freeze today's defaults
 * into a document that outlives them.
 */
export function validateParams(
  schema: Record<string, ParamSpec>,
  params: Record<string, unknown>,
  path: string,
  code = "design_bad_param",
): Issue[] {
  const issues: Issue[] = [];
  for (const name of Object.keys(params).sort()) {
    if (!(name in schema)) {
      issues.push(issue(code, `Unknown parameter '${name}'`, `${path}.${name}`));
    }
  }
  for (const [name, spec] of Object.entries(schema)) {
    const where = `${path}.${name}`;
    const value = params[name];
    if (!(name in params) || value === null || value === undefined) {
      if (spec.required === true) issues.push(issue(code, `'${name}' is required`, where));
      continue;
    }
    issues.push(...checkParam(spec, value, where, code));
  }
  return issues;
}

/**
 * One value against one arg spec — the closed `kind` set of MODULES §2.8.
 *
 * The editor runs this on the field a customer is typing into, so the message
 * under the input and the one in the sidebar are the same sentence.
 */
export function checkParam(
  spec: ParamSpec,
  value: unknown,
  path: string,
  code = "design_bad_param",
): Issue[] {
  if (spec.kind === "bool") {
    return typeof value === "boolean" ? [] : [issue(code, "Expected true or false", path)];
  }
  if (spec.kind === "int") {
    if (typeof value !== "number" || !Number.isInteger(value)) {
      return [issue(code, "Expected a whole number", path)];
    }
    return bounds(value, spec, path, code);
  }
  if (spec.kind === "float") {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return [issue(code, "Expected a number", path)];
    }
    return bounds(value, spec, path, code);
  }
  if (spec.kind === "string") {
    if (typeof value !== "string") return [issue(code, "Expected text", path)];
    if (value.length > LIMITS.paramText) {
      return [issue(code, `At most ${LIMITS.paramText} characters`, path)];
    }
    return bounds(value.length, spec, path, code, "characters");
  }
  if (spec.kind === "enum") {
    const choices = spec.choices ?? [];
    return choices.includes(value as string)
      ? []
      : [issue(code, `Must be one of ${choices.join(", ")}`, path)];
  }
  if (spec.kind === "url") return checkUrl(value, path, code, "Expected a link");
  // A reserved kind never reaches here: the upload lint refuses the manifest
  // first, so no design can name an action declaring one.
  return [issue(code, `Values of kind '${spec.kind}' cannot be checked yet`, path)];
}

function bounds(
  value: number,
  spec: ParamSpec,
  path: string,
  code: string,
  unit = "",
): Issue[] {
  const suffix = unit ? ` ${unit}` : "";
  if (typeof spec.min === "number" && value < spec.min) {
    return [issue(code, `Must be at least ${spec.min}${suffix}`, path)];
  }
  if (typeof spec.max === "number" && value > spec.max) {
    return [issue(code, `Must be at most ${spec.max}${suffix}`, path)];
  }
  return [];
}

/**
 * The status block's rows: the core five, and `<module>.<value>` beside them
 * (MODULES §7.2). A row that is neither stays `design_bad_value` at `rows`,
 * which is what the v1 validator already emitted.
 */
function validateRows(rows: unknown, path: string, index?: ContributionIndex): Issue[] {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [issue("design_bad_value", "Pick at least one status row", path)];
  }
  if (new Set(rows).size !== rows.length) {
    return [issue("design_bad_value", "Each status row may appear once", path)];
  }

  const issues: Issue[] = [];
  for (const [position, row] of rows.entries()) {
    if (STATUS_ROWS.includes(row as StatusRow)) continue;
    if (typeof row !== "string" || !MODULE_ROW_RE.test(row)) {
      // At the list rather than at the entry, by convention: it is the v1
      // message, the editor's picker cannot have produced it, and a document
      // this far from the spec is not worth reading further.
      return [...issues, issue("design_bad_value", `Unknown status row '${row}'`, path)];
    }
    issues.push(...validateModuleRow(row, `${path}[${position}]`, index));
  }
  return issues;
}

function validateModuleRow(row: string, path: string, index?: ContributionIndex): Issue[] {
  if (!index) return [];
  const [module, value] = [row.slice(0, row.indexOf(".")), row.slice(row.indexOf(".") + 1)];
  if (!index.known.has(module)) {
    return [issue("design_unknown_module", `This release has no module '${module}'`, path)];
  }
  if (!index.rows.get(module)?.has(value)) {
    return [
      issue("design_unknown_contribution", `'${module}' has no status row '${value}'`, path),
    ];
  }
  if (!index.enabled.has(module)) {
    return [
      warning(
        "design_module_disabled",
        `'${module}' is switched off, so this row will be missing`,
        path,
      ),
    ];
  }
  return [];
}

/** Asset ids the document uses — what "may this image be deleted?" asks. */
export function referencedAssets(spec: Spec): Set<string> {
  const used = new Set<string>();
  const background = spec.theme?.background;
  if (background && background.type === "image" && background.asset) used.add(background.asset);
  for (const block of spec.blocks ?? []) {
    if (block.type === "image" && block.asset) used.add(block.asset);
  }
  return used;
}

/** The block an issue's path points at, so the editor can jump to it. */
export function issueBlockId(path: string): string | null {
  const match = /^blocks\[([^\]]+)]/.exec(path);
  return match ? match[1] : null;
}
